import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Video, FileText, Download, Plus, Edit, Trash2, Save, X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';

const API_URL = window.location.origin;

const CATEGORIES = [
  { value: "basics", label: "Dispatching Basics", icon: BookOpen },
  { value: "loadboard", label: "Load Board Operations", icon: FileText },
  { value: "carrier_management", label: "Carrier Management", icon: FileText },
  { value: "shipper_management", label: "Shipper Management", icon: FileText },
  { value: "documentation", label: "Documentation & Compliance", icon: FileText },
  { value: "tracking", label: "Tracking & Updates", icon: Video },
  { value: "pricing", label: "Pricing & Margins", icon: FileText },
  { value: "scripts", label: "Scripts & Templates", icon: FileText },
  { value: "general", label: "General Information", icon: BookOpen }
];

const MATERIAL_TYPES = [
  { value: "document", label: "Document" },
  { value: "video", label: "Video" },
  { value: "pdf", label: "PDF" },
  { value: "script", label: "Script/Template" }
];

export default function Training() {
  const [user, setUser] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [expandedMaterial, setExpandedMaterial] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    category: 'basics',
    material_type: 'document',
    file_url: ''
  });
  const [saving, setSaving] = useState(false);

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUser(data);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
    fetchUser();
  }, []);

  // Fetch training materials
  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMaterials(data);
      }
    } catch (err) {
      console.error('Error fetching materials:', err);
      toast.error('Failed to load training materials');
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';

  // ---- Quick Reference Library (built-in + custom uploads) ----
  const [refLibrary, setRefLibrary] = useState([]);
  const [refCategory, setRefCategory] = useState('all');
  const [refUploadOpen, setRefUploadOpen] = useState(false);
  const [refUploadForm, setRefUploadForm] = useState({ title: '', description: '', category: 'general', file: null });
  const [refUploading, setRefUploading] = useState(false);
  const [refAnalytics, setRefAnalytics] = useState(null);
  const [refAnalyticsOpen, setRefAnalyticsOpen] = useState(false);
  const [refStreak, setRefStreak] = useState(null);

  const loadRefLibrary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/materials/library`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRefLibrary(data.materials || []);
      }
    } catch (err) { /* ignore */ }
  };
  const loadRefStreak = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/materials/streak`, { credentials: 'include' });
      if (res.ok) setRefStreak(await res.json());
    } catch (err) { /* ignore */ }
  };
  useEffect(() => { loadRefLibrary(); loadRefStreak(); }, []);

  const REF_CATEGORIES = [
    { key: 'all', label: 'All' },
    { key: 'rates', label: 'Rates' },
    { key: 'operations', label: 'Operations' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'general', label: 'General' },
  ];

  const uploadReference = async () => {
    if (!refUploadForm.file || !refUploadForm.title.trim()) {
      toast.error('Title and file are required');
      return;
    }
    setRefUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', refUploadForm.file);
      fd.append('title', refUploadForm.title);
      fd.append('description', refUploadForm.description);
      fd.append('category', refUploadForm.category || 'general');
      const res = await fetch(`${API_URL}/api/training/materials/upload`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Upload failed (${res.status})`);
        return;
      }
      toast.success('Reference uploaded');
      setRefUploadOpen(false);
      setRefUploadForm({ title: '', description: '', category: 'general', file: null });
      loadRefLibrary();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setRefUploading(false);
    }
  };

  const deleteReference = async (material) => {
    if (!window.confirm(`Delete "${material.title}" from the library?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/training/materials/custom/${material.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) { toast.error('Delete failed'); return; }
      toast.success('Deleted');
      loadRefLibrary();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const openAnalytics = async () => {
    setRefAnalyticsOpen(true);
    try {
      const res = await fetch(`${API_URL}/api/training/materials/analytics`, { credentials: 'include' });
      if (res.ok) setRefAnalytics(await res.json());
    } catch (err) {
      toast.error('Failed to load analytics');
    }
  };

  const downloadReference = async (materialOrKey) => {
    // Accept either a material object or a built-in filename key
    const url = typeof materialOrKey === 'string'
      ? `${API_URL}/api/training/materials/${materialOrKey}`
      : `${API_URL}${materialOrKey.download_url}`;
    const filename = typeof materialOrKey === 'string'
      ? materialOrKey
      : (materialOrKey.original_filename || `${materialOrKey.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.${materialOrKey.kind}`);
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        toast.error(`Download failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Downloaded ${filename}`);
      loadRefStreak();
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const openCreateDialog = () => {
    setSelectedMaterial(null);
    setFormData({
      title: '',
      description: '',
      content: '',
      category: 'basics',
      material_type: 'document',
      file_url: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (material) => {
    setSelectedMaterial(material);
    setFormData({
      title: material.title,
      description: material.description,
      content: material.content,
      category: material.category,
      material_type: material.material_type,
      file_url: material.file_url || ''
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (material) => {
    setSelectedMaterial(material);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    setSaving(true);
    try {
      const url = selectedMaterial 
        ? `${API_URL}/api/training/${selectedMaterial.id}`
        : `${API_URL}/api/training`;
      
      const method = selectedMaterial ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(selectedMaterial ? 'Material updated!' : 'Material created!');
        setDialogOpen(false);
        fetchMaterials();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save material');
      }
    } catch (err) {
      console.error('Error saving material:', err);
      toast.error('Failed to save material');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMaterial) return;
    
    try {
      const res = await fetch(`${API_URL}/api/training/${selectedMaterial.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Material deleted!');
        setDeleteDialogOpen(false);
        setSelectedMaterial(null);
        fetchMaterials();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete material');
      }
    } catch (err) {
      console.error('Error deleting material:', err);
      toast.error('Failed to delete material');
    }
  };

  const getCategoryInfo = (categoryValue) => {
    return CATEGORIES.find(c => c.value === categoryValue) || CATEGORIES[0];
  };

  const toggleExpand = (materialId) => {
    setExpandedMaterial(expandedMaterial === materialId ? null : materialId);
  };

  // Group materials by category
  const materialsByCategory = materials.reduce((acc, material) => {
    const cat = material.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(material);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dispatcher Training & Study Materials</h1>
          <p className="text-slate-600 mt-1">Essential resources for freight dispatching and brokerage operations</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={openCreateDialog} data-testid="add-training-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Training Material
          </Button>
        )}
      </div>

      {/* Welcome Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="bg-blue-600 p-3 rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Welcome to Dispatcher Training</h2>
              <p className="text-slate-700 mb-3">
                Master the skills needed to succeed as a freight dispatcher. These materials cover everything from basic concepts to advanced strategies.
              </p>
              <p className="text-sm text-slate-600">
                💡 Tip: Start with "Dispatching Basics" and work your way through each category
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-slate-600">Loading training materials...</span>
        </div>
      )}

      {/* No Materials State */}
      {!loading && materials.length === 0 && (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No Training Materials Yet</h3>
            <p className="text-slate-500 mb-4">
              {isSuperAdmin 
                ? "Get started by adding your first training material."
                : "Training materials will appear here once added by the administrator."}
            </p>
            {isSuperAdmin && (
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Material
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Training Materials by Category */}
      {!loading && Object.keys(materialsByCategory).length > 0 && (
        <div className="space-y-8">
          {CATEGORIES.filter(cat => materialsByCategory[cat.value]?.length > 0).map(category => (
            <div key={category.value} className="space-y-4">
              <div className="flex items-center gap-2">
                <category.icon className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">{category.label}</h2>
                <span className="bg-blue-100 text-blue-700 text-sm px-2 py-0.5 rounded-full">
                  {materialsByCategory[category.value].length}
                </span>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {materialsByCategory[category.value].map(material => (
                  <Card 
                    key={material.id} 
                    className="hover:shadow-md transition-shadow"
                    data-testid={`training-material-${material.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-lg">{material.title}</CardTitle>
                            <p className="text-sm text-slate-600 mt-1">{material.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                {MATERIAL_TYPES.find(t => t.value === material.material_type)?.label || 'Document'}
                              </span>
                              {material.file_url && (
                                <a 
                                  href={material.file_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  <Download className="w-3 h-3" />
                                  Download
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isSuperAdmin && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => openEditDialog(material)}
                                data-testid={`edit-material-${material.id}`}
                              >
                                <Edit className="w-4 h-4 text-slate-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => openDeleteDialog(material)}
                                data-testid={`delete-material-${material.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => toggleExpand(material.id)}
                            data-testid={`expand-material-${material.id}`}
                          >
                            {expandedMaterial === material.id 
                              ? <ChevronUp className="w-4 h-4" />
                              : <ChevronDown className="w-4 h-4" />
                            }
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    
                    {expandedMaterial === material.id && (
                      <CardContent className="pt-0">
                        <div className="border-t pt-4 mt-2">
                          <div 
                            className="prose prose-sm max-w-none text-slate-700"
                            dangerouslySetInnerHTML={{ __html: material.content.replace(/\n/g, '<br/>') }}
                          />
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Reference Guide — dynamic list of built-in + uploaded materials */}
      <Card className="border-2 border-green-200 bg-green-50">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-green-600" />
                Quick Reference Materials
              </CardTitle>
              {refStreak?.badge && (
                <p className="text-xs mt-1 font-medium text-amber-700" data-testid="streak-badge">
                  {refStreak.badge} · {refStreak.total_downloads} total downloads
                </p>
              )}
            </div>
            {isSuperAdmin && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openAnalytics} data-testid="open-reference-analytics">
                  <FileText className="w-4 h-4 mr-1" /> Analytics
                </Button>
                <Button
                  size="sm"
                  onClick={() => setRefUploadOpen(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="open-reference-upload"
                >
                  <Plus className="w-4 h-4 mr-1" /> Upload Reference
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {REF_CATEGORIES.map(cat => {
              const count = cat.key === 'all'
                ? refLibrary.length
                : refLibrary.filter(m => (m.category || 'general') === cat.key).length;
              const active = refCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => setRefCategory(cat.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active ? 'bg-green-600 text-white border-green-600'
                           : 'bg-white text-slate-700 border-slate-200 hover:border-green-400'
                  }`}
                  data-testid={`ref-cat-${cat.key}`}
                >
                  {cat.label} · {count}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {refLibrary
              .filter(m => refCategory === 'all' || (m.category || 'general') === refCategory)
              .map(m => (
              <div
                key={m.key || m.id}
                className="bg-white p-4 rounded-lg border border-green-200 flex flex-col"
                data-testid={`ref-card-${m.key || m.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-slate-900 mb-1">
                    {m.kind === 'xlsx' || m.kind === 'xls' ? '📊 ' : '📋 '}{m.title}
                  </p>
                  {isSuperAdmin && !m.builtin && (
                    <button
                      onClick={() => deleteReference(m)}
                      className="text-red-500 hover:text-red-700 flex-shrink-0"
                      title="Delete"
                      data-testid={`delete-ref-${m.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {m.category && (
                  <span className="inline-block text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded self-start mb-1">
                    {m.category}
                  </span>
                )}
                <p className="text-sm text-slate-600 mb-3 flex-1">{m.description || '—'}</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadReference(m.builtin ? m.key : m)}
                  data-testid={`download-${m.key || m.id}`}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download {m.kind === 'xlsx' || m.kind === 'xls' ? 'Excel' : (m.kind || 'File').toUpperCase()}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upload Reference Dialog (super_admin only) */}
      <Dialog open={refUploadOpen} onOpenChange={setRefUploadOpen}>
        <DialogContent className="max-w-md" data-testid="reference-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Upload Reference Material
            </DialogTitle>
            <DialogDescription>PDF, Excel, or Word — max 25 MB. Available to every team member instantly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Title *</Label>
              <Input
                value={refUploadForm.title}
                onChange={e => setRefUploadForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Broker Credit Score Guide"
                data-testid="ref-upload-title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={refUploadForm.description}
                onChange={e => setRefUploadForm(f => ({ ...f, description: e.target.value }))}
                placeholder="One-line description shown under the title"
                rows={2}
                data-testid="ref-upload-description"
              />
            </div>
            <div>
              <Label>Category</Label>
              <select
                value={refUploadForm.category}
                onChange={e => setRefUploadForm(f => ({ ...f, category: e.target.value }))}
                className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
                data-testid="ref-upload-category"
              >
                <option value="rates">Rates</option>
                <option value="operations">Operations</option>
                <option value="compliance">Compliance</option>
                <option value="general">General</option>
              </select>
            </div>
            <div>
              <Label>File *</Label>
              <Input
                type="file"
                accept=".pdf,.xlsx,.xls,.doc,.docx"
                onChange={e => setRefUploadForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                data-testid="ref-upload-file"
              />
              {refUploadForm.file && (
                <p className="text-[11px] text-slate-500 mt-1">
                  {refUploadForm.file.name} · {(refUploadForm.file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefUploadOpen(false)} disabled={refUploading}>Cancel</Button>
            <Button
              onClick={uploadReference}
              disabled={refUploading || !refUploadForm.title.trim() || !refUploadForm.file}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="ref-upload-submit"
            >
              {refUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={refAnalyticsOpen} onOpenChange={setRefAnalyticsOpen}>
        <DialogContent className="max-w-2xl" data-testid="reference-analytics-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Reference Download Analytics
            </DialogTitle>
            <DialogDescription>Total downloads: <strong>{refAnalytics?.total_downloads ?? '—'}</strong></DialogDescription>
          </DialogHeader>
          {!refAnalytics ? (
            <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">By Material</p>
                {refAnalytics.per_material.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No downloads yet.</p>
                ) : (
                  <div className="space-y-1">
                    {refAnalytics.per_material.map(r => (
                      <div key={r.key} className="flex items-center justify-between p-2 rounded bg-slate-50 border border-slate-200">
                        <span className="text-sm font-medium">{r.title || r.key}</span>
                        <span className="text-sm font-mono font-bold text-blue-700">{r.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Recent Downloads (25)</p>
                {refAnalytics.recent.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No recent activity.</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr className="text-left">
                          <th className="py-1.5 px-2">User</th>
                          <th className="py-1.5 px-2">Material</th>
                          <th className="py-1.5 px-2">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refAnalytics.recent.map(r => (
                          <tr key={r.id} className="border-t">
                            <td className="py-1 px-2">{r.user_name} <span className="text-slate-400">({r.user_role})</span></td>
                            <td className="py-1 px-2">{r.material_title}</td>
                            <td className="py-1 px-2 text-slate-500">{new Date(r.downloaded_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* FAQs */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="font-medium text-slate-900">Q: How long does it take to become a proficient dispatcher?</p>
            <p className="text-sm text-slate-600 mt-1">A: Most new dispatchers become comfortable with basic operations in 2-4 weeks. Mastering advanced strategies typically takes 3-6 months of consistent practice.</p>
          </div>
          <div>
            <p className="font-medium text-slate-900">Q: What are the most important skills for a dispatcher?</p>
            <p className="text-sm text-slate-600 mt-1">A: Communication, organization, problem-solving, and attention to detail are critical. You must be able to multitask and stay calm under pressure.</p>
          </div>
          <div>
            <p className="font-medium text-slate-900">Q: How do I handle difficult carriers or shippers?</p>
            <p className="text-sm text-slate-600 mt-1">A: Always remain professional, document everything, and focus on finding solutions. Refer to the "Carrier & Shipper Management" section for specific strategies.</p>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedMaterial ? 'Edit Training Material' : 'Add Training Material'}
            </DialogTitle>
            <DialogDescription>
              {selectedMaterial 
                ? 'Update the training material details below.'
                : 'Create a new training material for dispatchers.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., How to Book a Load"
                data-testid="material-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Short Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief summary of this material"
                data-testid="material-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="material-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={formData.material_type} 
                  onValueChange={(value) => setFormData({ ...formData, material_type: value })}
                >
                  <SelectTrigger data-testid="material-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {MATERIAL_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="file_url">File URL (Optional)</Label>
              <Input
                id="file_url"
                value={formData.file_url}
                onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                placeholder="https://example.com/document.pdf"
                data-testid="material-file-url-input"
              />
              <p className="text-xs text-slate-500">Link to an external document, video, or resource</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="content">Content * (Supports basic formatting)</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your training content here...

You can use line breaks to separate paragraphs.

• Use bullet points for lists
• Add step-by-step instructions
• Include examples and tips"
                className="min-h-[200px]"
                data-testid="material-content-input"
              />
              <p className="text-xs text-slate-500">
                This will be displayed when users expand the material. Use line breaks for paragraphs.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-material-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {selectedMaterial ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Training Material</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedMaterial?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-material-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
